import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { Box } from "@mui/material";

// Color palettes
const genderColors = {
  Male: "#00D9FF",
  Female: "#FF1CBE",
  Other: "#FFD60A"
};

const ageColors = [
  "#00D9FF",
  "#FF1CBE",
  "#FFD60A",
  "#4CAF50",
  "#FF5722",
  "#9C27B0",
  "#2196F3"
];

const RechartsPieChart = ({ lifeTimeCount, id, gender }) => {
  // Transform data based on type (gender or age)
  const transformData = () => {
    if (gender && lifeTimeCount?.gender) {
      // Gender data
      return Object.entries(lifeTimeCount.gender).map(([key, value]) => ({
        name: key,
        value: value || 0
      }));
    } else if (lifeTimeCount?.age) {
      // Age data
      return Object.entries(lifeTimeCount.age).map(([key, value]) => ({
        name: key,
        value: value || 0
      }));
    }
    return [];
  };

  const data = transformData();
  const colors = gender ? Object.values(genderColors) : ageColors;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = data.reduce((sum, entry) => sum + entry.value, 0);
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      
      return (
        <Box
          sx={{
            background: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
          }}
        >
          <p style={{ color: "#fff", margin: 0 }}>{payload[0].name}</p>
          <p style={{ color: payload[0].fill, margin: "5px 0 0 0" }}>
            Count: {payload[0].value.toLocaleString()}
          </p>
          <p style={{ color: "#888", margin: "5px 0 0 0" }}>
            {percentage}%
          </p>
        </Box>
      );
    }
    return null;
  };

  // Custom label
  const renderCustomLabel = (entry) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const percent = ((entry.value / total) * 100).toFixed(0);
    return `${percent}%`;
  };

  // Custom legend
  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <ul style={{ 
        listStyle: 'none', 
        padding: 0,
        margin: 0,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '15px'
      }}>
        {payload.map((entry, index) => (
          <li key={`item-${index}`} style={{ 
            display: 'flex', 
            alignItems: 'center',
            color: '#888'
          }}>
            <span style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              backgroundColor: entry.color,
              borderRadius: '50%',
              marginRight: 8
            }} />
            <span>{entry.value}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "400px", backgroundColor: "#090909", p: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={gender ? genderColors[entry.name] : colors[index % colors.length]} 
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            content={renderLegend}
            wrapperStyle={{ paddingTop: "20px" }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RechartsPieChart;